import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  BookOpen,
  ChevronRight,
  Edit3,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { parentRelationshipMeta } from '../../constants/relationships';
import StructuredDataTab from '../structured-data/StructuredDataTab';
import ContractFilesTab from './ContractFilesTab';
import ObligationsCard from './ObligationsCard';
import { ClauseEditForm, RelatedLinkRow } from './ContractDetailParts';
import { formatDate, formatDateTime, relativeTime } from './contractDetailUtils';

const KEY_TERM_TABS = [
  { key: 'keyRisks', label: 'Risks', tone: 'yellow', noun: 'risk', empty: 'None flagged.', placeholder: 'e.g. Uncapped liability for data breach' },
  { key: 'keyCommitments', label: 'Commitments', tone: 'blue', noun: 'commitment', empty: 'None.', placeholder: 'e.g. Give 60 days notice before renewal' },
  { key: 'financialTerms', label: 'Financial terms', tone: 'green', noun: 'financial term', empty: 'None.', placeholder: 'e.g. $50k/yr, net-30; 8% annual escalator' },
  { key: 'openIssues', label: 'Open items', tone: 'purple', noun: 'item', empty: 'No open items.', placeholder: 'e.g. Counterparty name still [TBD]' },
];

// Tabbed "Key terms" card: AI-surfaced risks / commitments / financial terms /
// open items. Each tab is an editable list — add, inline-edit, delete (legal).
// Regenerate re-runs the playbook-driven pass (the first three); open items
// re-derives on a full document re-scan, so it isn't touched by Regenerate.
function KeyTermsCard({ contract: c, canWrite, onSave, onRegenerate, onClear, regenerating }) {
  const [tabKey, setTabKey] = useState('keyRisks');
  const [editing, setEditing] = useState(null); // index being edited; list.length = new item
  const [draft, setDraft] = useState('');

  const tab = KEY_TERM_TABS.find((t) => t.key === tabKey);
  const list = Array.isArray(c[tabKey]) ? c[tabKey] : [];
  const keyTermCount = ['keyRisks', 'keyCommitments', 'financialTerms'].reduce((n, k) => n + (c[k] || []).length, 0);

  const switchTab = (k) => { setTabKey(k); setEditing(null); setDraft(''); };
  const cancelEdit = () => { setEditing(null); setDraft(''); };
  const commitEdit = () => {
    const text = draft.trim();
    if (!text) { cancelEdit(); return; }
    const next = editing >= list.length ? [...list, text] : list.map((x, idx) => (idx === editing ? text : x));
    onSave(tabKey, next);
    cancelEdit();
  };

  const editorRow = (
    <div style={{ padding: '10px 18px' }}>
      <textarea
        className="textarea"
        autoFocus
        rows={2}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commitEdit();
          if (e.key === 'Escape') cancelEdit();
        }}
        placeholder={tab.placeholder}
        style={{ width: '100%', resize: 'vertical', minHeight: 44, lineHeight: 1.5 }}
      />
      <div className="row" style={{ gap: 6, marginTop: 6, justifyContent: 'flex-end' }}>
        <button type="button" className="btn ghost xs" onClick={cancelEdit}>Cancel</button>
        <button type="button" className="btn primary xs" onClick={commitEdit} disabled={!draft.trim()}>Save</button>
      </div>
    </div>
  );

  return (
    <div className="card">
      <div className="card-header compact">
        <div className="row" style={{ gap: 8 }}>
          <AlertTriangle className="w-[13px] h-[13px]" style={{ color: 'var(--text-yellow)' }} />
          <span className="card-title">Key terms</span>
        </div>
        {canWrite && (
          <div className="row" style={{ gap: 6 }}>
            <button type="button" className="btn ghost xs" onClick={onRegenerate} disabled={regenerating}
              title="Re-run the AI key-term analysis (risks, commitments, financial terms)">
              {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Regenerate
            </button>
            {keyTermCount > 0 && (
              <button type="button" className="btn ghost xs" onClick={onClear} disabled={regenerating}
                title="Clear risks, commitments & financial terms" style={{ color: 'var(--text-critical)' }}>
                <Trash2 className="w-3.5 h-3.5" />
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, padding: '0 18px', borderBottom: '0.5px solid var(--border-primary)', overflowX: 'auto' }}>
        {KEY_TERM_TABS.map((t) => {
          const active = tabKey === t.key;
          const count = (c[t.key] || []).length;
          return (
            <button key={t.key} type="button" onClick={() => switchTab(t.key)} style={{
              padding: '9px 8px', fontSize: 12.5, background: 'none', border: 0, cursor: 'pointer', fontFamily: 'inherit',
              borderBottom: active ? '2px solid var(--color-gray-950)' : '2px solid transparent', marginBottom: '-0.5px',
              color: active ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: active ? 500 : 400,
              display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
            }}>
              {t.label}
              {count > 0 && <span className={`chip sq ${t.tone}`}>{count}</span>}
            </button>
          );
        })}
      </div>

      <div className="card-body flush">
        {list.length === 0 && editing === null && (
          <div className="muted" style={{ padding: '16px 18px', fontSize: 12.5 }}>{tab.empty}</div>
        )}
        {list.map((item, i) => (
          editing === i ? (
            <div key={i}>{editorRow}</div>
          ) : (
            <div key={i} style={{ padding: '9px 18px', borderBottom: '0.5px solid var(--border-primary)', display: 'grid', gridTemplateColumns: '22px 1fr auto', gap: 10, alignItems: 'flex-start' }}>
              <span className="mono tertiary" style={{ fontSize: 11, marginTop: 2 }}>{String(i + 1).padStart(2, '0')}</span>
              <div style={{ fontSize: 13, lineHeight: 1.55 }}>{item}</div>
              {canWrite && (
                <div className="row" style={{ gap: 2 }}>
                  <button type="button" className="icon-btn xs" onClick={() => { setEditing(i); setDraft(item); }} title="Edit" aria-label="Edit"><Edit3 className="w-3.5 h-3.5" /></button>
                  <button type="button" className="icon-btn xs" onClick={() => onSave(tabKey, list.filter((_, idx) => idx !== i))} title="Delete" aria-label="Delete" style={{ color: 'var(--text-critical)' }}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
          )
        ))}
        {editing !== null && editing >= list.length && editorRow}
        {canWrite && editing === null && (
          <div style={{ padding: '8px 18px' }}>
            <button type="button" className="btn ghost sm" onClick={() => { setEditing(list.length); setDraft(''); }}>
              <Plus className="w-3.5 h-3.5" />
              Add {tab.noun}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// The AI summary paragraph clamped to 4 lines with a Show more/less toggle
// (shown only when the text actually overflows), so Key terms stay above the
// fold without losing the long prose. `children` are extra actions rendered in
// the same row as the toggle, so the buttons share one baseline.
function ClampedSummary({ text, children }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);

  useEffect(() => {
    if (expanded) return;
    const el = ref.current;
    if (el) setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  return (
    <>
      <p
        ref={ref}
        style={{
          fontSize: 14, lineHeight: 1.65, color: 'var(--text-primary)', margin: 0, textWrap: 'pretty',
          ...(expanded ? {} : {
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }),
        }}
      >
        {text}
      </p>
      {(clamped || children) && (
        <div className="row" style={{ marginTop: 12 }}>
          {clamped && (
            <button type="button" className="btn ghost xs" onClick={() => setExpanded((v) => !v)}>
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
          {children}
        </div>
      )}
    </>
  );
}

export default function ContractDetailMainTabs({
  apiBase,
  beginClauseEdit,
  cancelClauseEdit,
  cancelFileEdit,
  canWriteContracts,
  canRunSchemas = canWriteContracts,
  clauseDraft,
  clauseError,
  clauseSaving,
  clauses,
  contract,
  contractId,
  deletingFileId,
  editingClauseNum,
  editingFileId,
  fileDraft,
  fileTiles,
  handleMarkAsExecuted,
  markingFileId,
  onRegenerateKeyRisks,
  onClearKeyRisks,
  onSaveKeyTerms,
  regeneratingKeyRisks,
  openClause,
  openFileDeleteConfirm,
  openFileEdit,
  openFilePreview,
  openUploadModal,
  saveClauseEdit,
  saveFileMeta,
  savingFileMeta,
  setClauseDraft,
  setDsDraft,
  setFileDraft,
  setOpenClause,
  setShowDetailedSummaryModal,
  structuredData,
  isAdmin,
  tab,
  timeline,
}) {
  const c = contract;
  const id = contractId;

  return (
    <>
        {/* Main column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {tab === 'overview' && (
            <>
              {/* AI summary — the Term card that used to sit here was
                  absorbed into the fact strip above the tabs (doc-106). */}
              <div className="card">
                <div className="card-header compact">
                  <div className="row" style={{ gap: 8 }}>
                    <Sparkles className="w-[13px] h-[13px]" style={{ color: 'var(--text-purple)' }} />
                    <span className="card-title">AI summary</span>
                    {c.summary && <span className="chip sq purple">Generated</span>}
                  </div>
                </div>
                <div className="card-body" style={{ paddingTop: 14 }}>
                  {(() => {
                    const detailedSummaryButton = (c.detailedSummary || canWriteContracts) ? (
                      <button
                        type="button"
                        onClick={() => { setDsDraft(c.detailedSummary || ''); setShowDetailedSummaryModal(true); }}
                        className="btn outline xs"
                      >
                        <BookOpen className="w-3 h-3" />
                        {c.detailedSummary ? 'Open detailed summary' : 'Add detailed summary'}
                      </button>
                    ) : null;
                    return c.summary ? (
                      <ClampedSummary text={c.summary}>{detailedSummaryButton}</ClampedSummary>
                    ) : (
                      <>
                        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                          No summary yet. {canWriteContracts && 'Use Scan document (in the More menu) to generate one.'}
                        </p>
                        {detailedSummaryButton && (
                          <div className="row" style={{ marginTop: 12 }}>{detailedSummaryButton}</div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Key terms — tabbed: risks / commitments / financial terms / open items */}
              <KeyTermsCard
                contract={c}
                canWrite={canWriteContracts}
                onSave={onSaveKeyTerms}
                onRegenerate={onRegenerateKeyRisks}
                onClear={onClearKeyRisks}
                regenerating={regeneratingKeyRisks}
              />

              {/* Docketed obligations — typed dated deadlines feeding the
                  reminder engine. Refetches when the Regenerate pass ends. */}
              <ObligationsCard
                contractId={id}
                canWrite={canWriteContracts}
                regenerating={regeneratingKeyRisks}
              />

              {/* Notes, if any */}
              {c.notes && (
                <div className="card">
                  <div className="card-header compact">
                    <span className="card-title">Notes</span>
                  </div>
                  <div className="card-body" style={{ paddingTop: 14 }}>
                    <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-primary)', margin: 0, whiteSpace: 'pre-wrap' }}>{c.notes}</p>
                  </div>
                </div>
              )}
            </>
          )}

          {tab === 'clauses' && (
            <div className="card">
              <div className="card-header compact">
                <span className="card-title">Extracted terms</span>
                <span className="muted" style={{ fontSize: 11 }}>Click a section to view the extracted values</span>
              </div>
              <div className="card-body flush">
                {clauses.map((cl, i) => {
                  const isOpen = openClause === cl.num;
                  const isEditing = editingClauseNum === cl.num;
                  const handleHeaderClick = () => {
                    if (isEditing) return; // ignore clicks while editing to avoid accidental collapse
                    setOpenClause(isOpen ? null : cl.num);
                  };
                  return (
                    <div key={cl.num} style={{ borderBottom: i === clauses.length - 1 ? 0 : '0.5px solid var(--border-primary)' }}>
                      <button
                        onClick={handleHeaderClick}
                        style={{
                          width: '100%',
                          display: 'grid',
                          gridTemplateColumns: '14px 1fr auto',
                          gap: 14,
                          padding: '14px 24px',
                          alignItems: 'center',
                          textAlign: 'left',
                          background: isOpen ? 'var(--surface-hover)' : 'transparent',
                          transition: 'background 120ms',
                          cursor: isEditing ? 'default' : 'pointer',
                        }}
                      >
                        <ChevronRight
                          className="w-[12px] h-[12px]"
                          style={{ opacity: 0.5, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 160ms' }}
                        />
                        <span style={{ fontSize: 13, fontWeight: 450 }}>{cl.label}</span>
                        {cl.flag === 'attention' ? (
                          <span className="chip sq yellow">Attention</span>
                        ) : cl.flag === 'risk' ? (
                          <span className="chip sq red">Risk</span>
                        ) : <span />}
                      </button>
                      <div style={{ display: 'grid', gridTemplateRows: isOpen ? '1fr' : '0fr', transition: 'grid-template-rows 220ms var(--motion-ease)' }}>
                        <div style={{ overflow: 'hidden' }}>
                          <div style={{ padding: '8px 32px 24px 52px' }}>
                            {isEditing ? (
                              <ClauseEditForm
                                clause={cl}
                                draft={clauseDraft}
                                setDraft={setClauseDraft}
                                saving={clauseSaving}
                                error={clauseError}
                                onSave={() => saveClauseEdit(cl)}
                                onCancel={cancelClauseEdit}
                              />
                            ) : (
                              <>
                                {cl.filled.length === 0 ? (
                                  <div className="muted" style={{ fontSize: 13, fontStyle: 'italic' }}>
                                    No values extracted for this section.
                                  </div>
                                ) : (
                                  <dl style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(160px, max-content) 1fr',
                                    gap: '14px 40px',
                                    margin: 0,
                                    fontSize: 13,
                                    lineHeight: 1.6,
                                  }}>
                                    {cl.filled.map(fd => {
                                      const disp = fd.display;
                                      const asStr = disp == null ? '' : String(disp);
                                      const isLong = typeof asStr === 'string' && asStr.length > 40;
                                      return (
                                        <React.Fragment key={fd.label}>
                                          <dt className="tertiary" style={{ fontSize: 12, paddingTop: 1 }}>{fd.label}</dt>
                                          <dd style={{
                                            margin: 0,
                                            color: 'var(--text-primary)',
                                            fontStyle: isLong ? 'italic' : 'normal',
                                            whiteSpace: 'pre-wrap',
                                          }}>
                                            {isLong ? `"${asStr}"` : asStr}
                                          </dd>
                                        </React.Fragment>
                                      );
                                    })}
                                  </dl>
                                )}
                                {canWriteContracts && (
                                  <div className="row" style={{ marginTop: 20, gap: 8 }}>
                                    <button className="btn outline sm" onClick={() => beginClauseEdit(cl)}>
                                      <Edit3 className="w-3.5 h-3.5" /> Edit section
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === 'data' && structuredData && (
            <StructuredDataTab
              sd={structuredData}
              canRunSchemas={canRunSchemas}
              canWriteContracts={canWriteContracts}
              isAdmin={isAdmin}
            />
          )}

          {tab === 'timeline' && (
            <div className="card">
              <div className="card-body" style={{ paddingTop: 20 }}>
                {timeline.length === 0 ? (
                  <div className="muted" style={{ fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
                    No activity yet.
                  </div>
                ) : (
                  <div style={{ position: 'relative', paddingLeft: 24 }}>
                    <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 1, background: 'var(--border-primary)' }} />
                    {timeline.map((e, i) => (
                      <div key={i} style={{ position: 'relative', padding: '10px 0' }}>
                        <span style={{
                          position: 'absolute',
                          left: -22,
                          top: 14,
                          width: 9,
                          height: 9,
                          borderRadius: 99,
                          background: 'var(--surface-base)',
                          border: `1.5px solid ${i === 0 ? 'var(--color-green-500)' : 'var(--border-secondary)'}`,
                        }} />
                        <div style={{ fontSize: 13 }}>
                          <strong style={{ fontWeight: 500 }}>{e.who}</strong>{' '}
                          <span style={{ color: 'var(--text-secondary)' }}>{e.text}</span>
                        </div>
                        <div className="muted mono" style={{ fontSize: 11, marginTop: 2 }}>
                          {relativeTime(e.when)} · {formatDateTime(e.when)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'files' && (
            <ContractFilesTab
              apiBase={apiBase}
              canWriteContracts={canWriteContracts}
              cancelFileEdit={cancelFileEdit}
              contractId={id}
              deletingFileId={deletingFileId}
              editingFileId={editingFileId}
              fileDraft={fileDraft}
              fileTiles={fileTiles}
              handleMarkAsExecuted={handleMarkAsExecuted}
              markingFileId={markingFileId}
              openFileDeleteConfirm={openFileDeleteConfirm}
              openFileEdit={openFileEdit}
              openFilePreview={openFilePreview}
              openUploadModal={openUploadModal}
              saveFileMeta={saveFileMeta}
              savingFileMeta={savingFileMeta}
              setFileDraft={setFileDraft}
            />
          )}

          {tab === 'related' && (
            <div className="card">
              <div className="card-body flush">
                {c.parent && (
                  (() => {
                    const meta = parentRelationshipMeta({
                      relationshipType: c.parentRelationshipType || c.parent.relationshipType,
                      contractType: c.contractType,
                      parentReferenceText: c.parentReferenceText,
                      title: c.title,
                      shortName: c.shortName,
                    });
                    return (
                      <RelatedLinkRow
                        label={meta.parentLabel}
                        to={`/contracts/${c.parent.id}`}
                        title={c.parent.title}
                        hint={[c.parent.contractType, c.parent.counterparty].filter(Boolean).join(' · ')}
                        chip={<span className={`chip sq ${meta.chipClass}`}>{meta.chipLabel}</span>}
                      />
                    );
                  })()
                )}
                {Array.isArray(c.amendments) && c.amendments.map(a => {
                  const meta = parentRelationshipMeta({
                    relationshipType: a.relationshipType,
                    contractType: a.contractType,
                    title: a.title,
                    shortName: a.shortName,
                  });
                  return (
                    <RelatedLinkRow
                      key={a.id}
                      label={meta.childLabel}
                      to={`/contracts/${a.id}`}
                      title={a.title}
                      hint={a.effectiveDate ? `Effective ${formatDate(a.effectiveDate)}` : null}
                      chip={<span className={`chip sq ${meta.chipClass}`}>{meta.chipLabel}</span>}
                    />
                  );
                })}
                {/* Renewal chain — this contract supersedes c.renewalOf,
                    and is itself superseded by anything in c.renewedBy. */}
                {c.renewalOf && (
                  <RelatedLinkRow
                    label="Renewal of"
                    to={`/contracts/${c.renewalOf.id}`}
                    title={c.renewalOf.title}
                    hint={[
                      c.renewalOf.contractType,
                      c.renewalOf.counterparty,
                      c.renewalOf.expirationDate ? `Expired ${formatDate(c.renewalOf.expirationDate)}` : null,
                    ].filter(Boolean).join(' · ')}
                    chip={<span className="chip sq blue">Predecessor</span>}
                  />
                )}
                {Array.isArray(c.renewedBy) && c.renewedBy.map((r) => (
                  <RelatedLinkRow
                    key={r.id}
                    label="Renewed by"
                    to={`/contracts/${r.id}`}
                    title={r.title}
                    hint={[
                      r.contractType,
                      r.effectiveDate ? `Effective ${formatDate(r.effectiveDate)}` : null,
                    ].filter(Boolean).join(' · ')}
                    chip={<span className="chip sq green">Successor</span>}
                  />
                ))}
                {c.sourceRequestId && (
                  <RelatedLinkRow
                    label="Created from TPRM request"
                    to={`/tprm/${c.sourceRequestId}`}
                    title="View originating request"
                    chip={<span className="chip sq blue">TPRM</span>}
                  />
                )}
                {c.parentReferenceText && !c.parent && (
                  <div style={{ padding: 20 }}>
                    <div className="callout blue" style={{ alignItems: 'flex-start' }}>
                      <div className="callout-icon"><Sparkles className="w-4 h-4" /></div>
                      <div className="callout-body">
                        <strong>AI detected a parent reference</strong>
                        <div style={{ fontSize: 12, fontStyle: 'italic', marginTop: 4 }}>"{c.parentReferenceText}"</div>
                        <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                          {canWriteContracts ? 'Open Edit to link the matching contract.' : 'Not yet linked.'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {!c.parent
                  && (!c.amendments || c.amendments.length === 0)
                  && !c.renewalOf
                  && (!c.renewedBy || c.renewedBy.length === 0)
                  && !c.sourceRequestId
                  && !c.parentReferenceText && (
                  <div className="muted" style={{ padding: 40, textAlign: 'center', fontSize: 13 }}>
                    No related contracts or requests.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
    </>
  );
}
